import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import {
  formatCurrency,
  formatNumber,
  formatDuration,
} from "../../../../library/utils/numberFormatting";
import { Badge, Tooltip, Button, Checkbox } from "@fluentui/react-components";
import {
  CheckmarkCircleRegular,
  ErrorCircleRegular,
  WarningRegular,
  InfoRegular,
  EditRegular,
} from "@fluentui/react-icons";
import EditPremiumDialog from "../../../../components/EditPremiumDialog/EditPremiumDialog";
import { EditDeductiblesDialog } from "../../../../components/EditDeductiblesDialog/EditDeductiblesDialog";
import { EditTransportingExpensesDialog } from "../../../../components/EditTransportingExpensesDialog/EditTransportingExpensesDialog";
import type {
  QuoteUpdateDto,
  ExcludedItemDto,
} from "../../../../library/types/types";

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 99.9%;
  height: 100%;
  gap: 6px;
`;

const TableContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.semanticColors.bodyFrameDivider};
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: auto;
  background: ${({ theme }) => theme.semanticColors.bodyBackground};
  width: 100%;
  height: 100%;
  margin-bottom: 16px;

  /* Remove inner table borders that overlap with container */
  table {
    th:first-child,
    td:first-child {
      border-left: none;
    }

    thead tr:first-child th {
      border-top: none;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  min-width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
`;

const TableHeader = styled.thead`
  background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};

  th {
    padding: 8px 4px;
    font-weight: 600;
    font-size: 11px;
    color: ${({ theme }) => theme.semanticColors.bodySubtext};
    border-bottom: 2px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    border-right: 1px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    border-top: 1px solid
      ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    text-align: center;
    vertical-align: top;
    min-width: 85px;
    max-width: 120px;

    &:first-child {
      width: 140px;
      min-width: 140px;
      text-align: left;
      position: sticky;
      left: 0;
      background: ${({ theme }) => theme.semanticColors.bodyStandoutBackground};
      z-index: 2;
      border-left: 1px solid
        ${({ theme }) => theme.semanticColors.bodyFrameDivider};
    }
  }
`;

const InsurerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const InsurerName = styled.div`
  font-size: 12px;
  font-weight: 600;
  word-break: break-word;
  text-align: center;
`;

const TableBody = styled.tbody`
  tr:nth-child(odd) {
    background: ${({ theme }) => theme.semanticColors.bodyBackground};

    td:first-child {
      background: ${({ theme }) => theme.semanticColors.bodyBackground};
    }
  }

  tr:nth-child(even) {
    background: ${({ theme }) =>
      theme.isDark
        ? theme.semanticColors.bodyBackground
        : theme.semanticColors.bodyStandoutBackground};

    td:first-child {
      background: ${({ theme }) =>
        theme.isDark
          ? theme.semanticColors.bodyBackground
          : theme.semanticColors.bodyStandoutBackground};
    }
  }

  tr:hover {
    background: ${({ theme }) => theme.semanticColors.bodyBackgroundHovered};

    td:first-child {
      background: ${({ theme }) => theme.semanticColors.bodyBackgroundHovered};
    }
  }
`;

const TableRow = styled.tr<{ isSection?: boolean; hasError?: boolean }>`
  ${({ isSection, theme }) =>
    isSection &&
    `
    background: ${
      theme.isDark ? theme.semanticColors.bodyBackgroundHovered : "#f0f0f0"
    } !important;
    font-weight: 600;
    
    td:first-child {
      background: ${
        theme.isDark ? theme.semanticColors.bodyBackgroundHovered : "#f0f0f0"
      } !important;
    }
  `}

  ${({ hasError, theme }) =>
    hasError &&
    `
    background: ${
      theme.isDark ? "rgba(255, 77, 79, 0.15)" : "#fff2f0"
    } !important;
    
    td:first-child {
      background: ${
        theme.isDark ? "rgba(255, 77, 79, 0.15)" : "#fff2f0"
      } !important;
    }
  `}

  td {
    padding: 6px 4px;
    border-bottom: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    border-right: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    text-align: center;
    vertical-align: middle;
    font-size: 11px;
    color: ${({ theme }) => theme.semanticColors.bodyText};
    max-width: 120px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:first-child {
      text-align: left;
      font-weight: 600;
      position: sticky;
      left: 0;
      z-index: 1;
      padding-left: 12px;
      width: 140px;
      min-width: 140px;
      white-space: normal;
      border-left: 1px solid ${({ theme }) => theme.semanticColors.bodyDivider};
    }

    &.selected-variant {
      background: ${({ theme }) =>
        theme.isDark ? "rgba(0, 120, 212, 0.2)" : "#e6f7ff"} !important;
      position: relative;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        border: 2px solid
          ${({ theme }) => (theme.isDark ? "#4db8ff" : "#1890ff")};
        pointer-events: none;
      }
    }
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const RowLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  gap: 2px;
  font-size: 11px;

  input[type="radio"] {
    cursor: pointer;
    margin: 0;
    width: 14px;
    height: 14px;
  }
`;

const PremiumValue = styled.div`
  font-size: 11px;
`;

const PremiumCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  position: relative;

  &:hover .edit-icon-button {
    opacity: 1;
  }
`;

const EditIconButton = styled(Button)`
  position: absolute;
  top: 2px;
  right: 2px;
  min-width: 20px;
  height: 20px;
  padding: 2px;
  opacity: 0;
  transition: opacity 0.2s;
  z-index: 10;

  &.always-visible {
    opacity: 1;
  }
`;

const UpdatedValue = styled.span`
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.isDark ? "#ff7875" : "#ff4d4f")};
  max-width: 150px;
  margin: 0 auto;
  line-height: 1.3;
`;

const WarningMessage = styled.div`
  font-size: 11px;
  color: ${({ theme }) => (theme.isDark ? "#ffc53d" : "#d46b08")};
  max-width: 150px;
  margin: 0 auto;
  line-height: 1.3;
`;

const SecurityNote = styled.div`
  font-size: 10px;
  color: #1890ff;
  max-width: 150px;
  margin: 4px auto 0;
  line-height: 1.3;
  font-style: italic;
`;

const NoDataCell = styled.div`
  color: ${({ theme }) => theme.semanticColors.bodySubtext};
  font-style: italic;
`;

const ExcludedCell = styled.td<{ excluded?: boolean }>`
  ${({ excluded, theme }) =>
    excluded &&
    `
    opacity: 0.4;
    background-color: ${theme.semanticColors.bodyStandoutBackground} !important;
    
    * {
      text-decoration: line-through;
      color: ${theme.semanticColors.bodySubtext} !important;
    }
  `}
`;

const ExcludedHeader = styled.th<{ excluded?: boolean }>`
  ${({ excluded, theme }) =>
    excluded &&
    `
    opacity: 0.5;
    background-color: ${theme.semanticColors.bodyStandoutBackground} !important;
    
    * {
      color: ${theme.semanticColors.bodySubtext} !important;
    }
  `}
`;

const InsurerCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 4px;

  /* Make checkbox more subtle with muted teal */
  .fui-Checkbox {
    --colorCompoundBrandBackground: #4a9a9a;
    --colorCompoundBrandBackgroundHover: #3d8585;
    --colorCompoundBrandBackgroundPressed: #357575;
  }

  .fui-Checkbox__indicator {
    border-color: #7ab5b5;
  }

  .fui-Checkbox:checked .fui-Checkbox__indicator {
    background-color: #4a9a9a;
    border-color: #4a9a9a;
  }
`;

const VariantCheckboxWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  /* Make checkbox more subtle with muted teal */
  .fui-Checkbox {
    --colorCompoundBrandBackground: #4a9a9a;
    --colorCompoundBrandBackgroundHover: #3d8585;
    --colorCompoundBrandBackgroundPressed: #357575;
  }

  .fui-Checkbox__indicator {
    border-color: #7ab5b5;
  }

  .fui-Checkbox:checked .fui-Checkbox__indicator {
    background-color: #4a9a9a;
    border-color: #4a9a9a;
  }
`;

const NoQuotesMessage = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${({ theme }) => theme.semanticColors.bodySubtext};

  svg {
    font-size: 48px;
    opacity: 0.3;
    margin-bottom: 16px;
  }

  h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: ${({ theme }) => theme.semanticColors.bodyText};
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

// Types
interface CascoPremium {
  Year: number;
  Payments: number;
  ClientPremium: number;
  InsurerPremium: number;
  InsurerDiscount: number;
  CommissionDiscount: number;
  DiscountDescription?: string | null;
  Period: number;
}

interface CascoVariant {
  ID: number;
  Deductible: string;
  DeductibleSecondAccident: string;
  DeductibleTheft: string;
  DeductibleTotalLoss: string;
  DeductibleGlass: string;
  SecuritySystem?: string | null;
  Premiums: CascoPremium[];
  YearCount: number;
  HasPrices: boolean;
  /** Synthetic variant shown when the insurer returned fewer variants, so values can be entered manually */
  IsPlaceholder?: boolean;
}

// Every CASCO insurer always shows this many variants (backend adds empty ones for new quotes)
export const CASCO_VARIANTS_COUNT = 2;

const PLACEHOLDER_VARIANT: CascoVariant = {
  ID: 1,
  Deductible: "",
  DeductibleSecondAccident: "",
  DeductibleTheft: "",
  DeductibleTotalLoss: "",
  DeductibleGlass: "",
  Premiums: [],
  YearCount: 1,
  HasPrices: false,
  IsPlaceholder: true,
};

// Pad to CASCO_VARIANTS_COUNT variants (e.g. offers saved before the backend did it).
// Variant IDs are 1-based, so placeholders take the same IDs missing real variants would have.
const withPlaceholderVariants = (variants: CascoVariant[]): CascoVariant[] => {
  if (variants.length >= CASCO_VARIANTS_COUNT) return variants;
  const padded = [...variants];
  const usedIds = new Set(variants.map((v) => v.ID));
  for (let id = 1; padded.length < CASCO_VARIANTS_COUNT; id++) {
    if (!usedIds.has(id)) padded.push({ ...PLACEHOLDER_VARIANT, ID: id });
  }
  return padded;
};

interface CascoError {
  Code: number;
  Description?: string | null;
  Message: string;
}

interface CascoException {
  Code: number;
  GenericMessage: string;
  GenericStackTrace?: string;
}

interface CascoQuoteData {
  Variants: CascoVariant[];
  TransportingExpenses?: string | number | null;
  Territory?: string | null;
  HasPrices: boolean;
  Insurer: string;
  Calculator: string;
  ErrorList: CascoError[];
  ExceptionList?: CascoException[];
  Duration: number;
  InsurerRequest: string[];
  InsurerResponse: string[];
  HasErrors: boolean;
  HasInsurerMessages: boolean;
  HasInsurerWarnings: boolean;
  HasInsurerErrors: boolean;
  HasGenericErrors: boolean;
  HasValidationErrors: boolean;
}

interface CascoQuotesDisplayProps {
  quotes: CascoQuoteData[];
  cascoSaved?: CascoQuoteData[];
  /** Originally requested insurers from the calculation request - all will be displayed as columns */
  requestedInsurers?: string[];
  /** Originally requested payment options from the calculation request - all will be displayed as rows */
  requestedPayments?: number[];
  invoiceData?: {
    Insurer: string;
    Period: number;
    Payments: number;
    VariantID?: number;
  } | null;
  onSelectionChange?: (
    selection: {
      insurer: string;
      variantIndex: number;
      variantID: number;
      premium: number | null;
      payments: number;
      period?: number;
      deductible?: string;
      deductibleSecondAccident?: string;
      deductibleTheft?: string;
      deductibleTotalLoss?: string;
      deductibleGlass?: string;
      securitySystem?: string;
      territory?: string;
      transportingExpenses?: string;
      insurerDiscount?: number;
      commissionDiscount?: number;
    } | null,
  ) => void;
  /** Updated quotes from backend (CascoUpdated) */
  updatedQuotes?: QuoteUpdateDto[];
  /** Map of original premium values before updates (key: "Insurer-VariantID-Payments") */
  originalPremiums?: Map<string, number>;
  /** Callback when user edits a quote value */
  onQuoteUpdate?: (
    update: QuoteUpdateDto,
    originalPremium: number | null,
  ) => void;
  /** Callback when user resets deductibles to original values */
  onResetDeductibles?: (insurer: string, variantID: number) => void;
  /** Excluded insurers/variants for invoice generation */
  excludedItems?: ExcludedItemDto[];
  /** Callback when exclusions change */
  onExclusionChange?: (exclusions: ExcludedItemDto[]) => void;
  /** Whether editing is allowed (disabled after invoice/save) */
  canEdit?: boolean;
}

const CascoQuotesDisplay = ({
  quotes,
  cascoSaved = [],
  requestedInsurers,
  requestedPayments,
  invoiceData,
  onSelectionChange,
  updatedQuotes = [],
  originalPremiums,
  onQuoteUpdate,
  onResetDeductibles,
  excludedItems = [],
  onExclusionChange,
  canEdit = true,
}: CascoQuotesDisplayProps) => {
  const { t } = useTranslation();

  // Edit dialog state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingQuote, setEditingQuote] = useState<{
    insurer: string;
    period: number;
    payments: number;
    variantID: number | null;
    originalPremium: number | null;
    currentPremium: number | null;
    deductible?: string | null;
    deductibleSecondAccident?: string | null;
    deductibleTheft?: string | null;
    deductibleTotalLoss?: string | null;
    deductibleGlass?: string | null;
    transportingExpenses?: string | null;
  } | null>(null);

  // Deductibles dialog state
  const [isDeductiblesDialogOpen, setIsDeductiblesDialogOpen] = useState(false);
  const [editingDeductibles, setEditingDeductibles] = useState<{
    insurer: string;
    variantID: number;
    variantIndex: number;
    deductible?: string | null;
    deductibleSecondAccident?: string | null;
    deductibleGlass?: string | null;
    deductibleTheft?: string | null;
    deductibleTotalLoss?: string | null;
  } | null>(null);

  // Transporting Expenses dialog state
  const [
    isTransportingExpensesDialogOpen,
    setIsTransportingExpensesDialogOpen,
  ] = useState(false);
  const [editingTransportingExpenses, setEditingTransportingExpenses] =
    useState<{
      insurer: string;
      transportingExpenses: string | null;
    } | null>(null);

  // Use quotes directly without merging logic
  const mergedQuotes = quotes;

  // State for single selected variant across all insurers (format: "InsurerName-VariantIndex-Payments")
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);

  // Track if we've made the initial selection to avoid re-selecting on every render
  const hasInitializedRef = useRef(false);
  // Track the previous invoiceData to detect when invoice is issued
  const prevInvoiceDataRef = useRef(invoiceData);

  // Helper: Check if an insurer is excluded (entire insurer)
  const isInsurerExcluded = useCallback(
    (insurer: string): boolean => {
      return excludedItems.some(
        (item) =>
          item.Insurer.toLowerCase() === insurer.toLowerCase() &&
          (item.VariantID === undefined || item.VariantID === null),
      );
    },
    [excludedItems],
  );

  // Helper: Check if a specific variant is excluded
  const isVariantExcluded = useCallback(
    (insurer: string, variantID: number): boolean => {
      // Check if entire insurer is excluded
      if (isInsurerExcluded(insurer)) return true;
      // Check if specific variant is excluded
      return excludedItems.some(
        (item) =>
          item.Insurer.toLowerCase() === insurer.toLowerCase() &&
          item.VariantID === variantID,
      );
    },
    [excludedItems, isInsurerExcluded],
  );

  // Handler: Toggle insurer exclusion (excludes all variants)
  const handleInsurerExclusionToggle = useCallback(
    (insurer: string) => {
      if (!onExclusionChange) return;

      const isCurrentlyExcluded = isInsurerExcluded(insurer);

      if (isCurrentlyExcluded) {
        // Remove insurer exclusion (and any variant exclusions for this insurer)
        const newExclusions = excludedItems.filter(
          (item) => item.Insurer.toLowerCase() !== insurer.toLowerCase(),
        );
        onExclusionChange(newExclusions);
      } else {
        // Add insurer exclusion (with variantID: null = excludes entire insurer)
        const newExclusions = [
          ...excludedItems.filter(
            (item) => item.Insurer.toLowerCase() !== insurer.toLowerCase(),
          ),
          { Insurer: insurer, VariantID: null },
        ];
        onExclusionChange(newExclusions);
      }
    },
    [excludedItems, isInsurerExcluded, onExclusionChange],
  );

  // Handler: Toggle variant exclusion
  const handleVariantExclusionToggle = useCallback(
    (insurer: string, variantID: number) => {
      if (!onExclusionChange) return;

      const isCurrentlyExcluded = excludedItems.some(
        (item) =>
          item.Insurer.toLowerCase() === insurer.toLowerCase() &&
          item.VariantID === variantID,
      );

      if (isCurrentlyExcluded) {
        // Remove variant exclusion
        const newExclusions = excludedItems.filter(
          (item) =>
            !(
              item.Insurer.toLowerCase() === insurer.toLowerCase() &&
              item.VariantID === variantID
            ),
        );
        onExclusionChange(newExclusions);
      } else {
        // Add variant exclusion
        let newExclusions = [
          ...excludedItems,
          { Insurer: insurer, VariantID: variantID },
        ];

        // Check if ALL variants for this insurer are now excluded
        const insurerQuote = mergedQuotes.find(
          (q) => q.Insurer.toLowerCase() === insurer.toLowerCase(),
        );
        if (insurerQuote) {
          const allVariantIDs = withPlaceholderVariants(
            insurerQuote.Variants,
          ).map((v) => v.ID);
          const excludedVariantIDs = newExclusions
            .filter(
              (item) =>
                item.Insurer.toLowerCase() === insurer.toLowerCase() &&
                item.VariantID !== undefined &&
                item.VariantID !== null,
            )
            .map((item) => item.VariantID);

          const allVariantsExcluded = allVariantIDs.every((id) =>
            excludedVariantIDs.includes(id),
          );

          if (allVariantsExcluded) {
            // Remove individual variant exclusions and add insurer-level exclusion
            newExclusions = newExclusions.filter(
              (item) => item.Insurer.toLowerCase() !== insurer.toLowerCase(),
            );
            newExclusions.push({ Insurer: insurer, VariantID: null });
          }
        }

        onExclusionChange(newExclusions);
      }
    },
    [excludedItems, mergedQuotes, onExclusionChange],
  );

  // Check if a specific radio option matches the invoice
  const isInvoicedOption = (
    insurer: string,
    variantID: number,
    payments: number,
  ): boolean => {
    // console.log(
    //   invoiceData,
    //   { insurer, variantID, payments },
    //   invoiceData?.Insurer.toLowerCase() === insurer.toLowerCase() &&
    //     invoiceData?.Payments === payments &&
    //     invoiceData?.VariantID === variantID
    // );
    if (!invoiceData) return false;
    return (
      invoiceData.Insurer.toLowerCase() === insurer.toLowerCase() &&
      invoiceData.Payments === payments &&
      invoiceData.VariantID === variantID
    );
  };

  // Find the invoiced variant if invoice exists
  const invoicedVariant = useMemo(() => {
    if (!invoiceData) return null;

    // The invoice identifies the selection (insurer + variant + payments), whether the premium
    // came from the insurer or from a manual update that didn't exist in the original offer
    const quote = mergedQuotes.find(
      (q) => q.Insurer.toLowerCase() === invoiceData.Insurer.toLowerCase(),
    );
    if (!quote) return null;

    // Match against the displayed variants (including placeholders)
    const variantIndex = withPlaceholderVariants(quote.Variants).findIndex(
      (v) => v.ID === invoiceData.VariantID,
    );

    const selectedKey =
      variantIndex === -1
        ? null
        : `${quote.Insurer}-${variantIndex}-${invoiceData.Payments}`;

    // console.log(
    //   "CASCO invoicedVariant:",
    //   selectedKey,
    //   "invoiceData:",
    //   invoiceData
    // );
    return selectedKey;
  }, [mergedQuotes, invoiceData]);

  // Calculate the variant with lowest premium
  const lowestPremiumVariant = useMemo(() => {
    let lowestPremium = Infinity;
    let selectedKey: string | null = null;

    mergedQuotes.forEach((quote) => {
      quote.Variants.forEach((variant, variantIndex) => {
        if (variant.HasPrices && variant.Premiums.length > 0) {
          // Get the annual premium (1 payment)
          const annualPremium = variant.Premiums.find((p) => p.Payments === 1);
          if (annualPremium && annualPremium.ClientPremium < lowestPremium) {
            lowestPremium = annualPremium.ClientPremium;
            selectedKey = `${quote.Insurer}-${variantIndex}-1`;
          }
        }
      });
    });

    return selectedKey;
  }, [mergedQuotes]);

  // Set initial selection only once when component mounts or quotes first become available
  // Prioritize invoiced variant if invoice exists
  useEffect(() => {
    // Always update selection when invoiceData changes (invoice is issued)
    // This ensures the invoiced variant is selected even if we already initialized
    const invoiceDataChanged = prevInvoiceDataRef.current !== invoiceData;
    prevInvoiceDataRef.current = invoiceData;

    // console.log("CASCO Selection useEffect:", {
    //   invoiceDataChanged,
    //   invoicedVariant,
    //   lowestPremiumVariant,
    //   currentSelection: selectedVariant,
    //   hasInitialized: hasInitializedRef.current,
    // });

    if (invoiceDataChanged && invoicedVariant) {
      // Invoice was just issued - force select the invoiced variant
      // console.log("Setting selection to invoiced variant:", invoicedVariant);
      setSelectedVariant(invoicedVariant);
      return;
    }

    if (!hasInitializedRef.current) {
      if (invoicedVariant) {
        // console.log("Initial selection - invoiced variant:", invoicedVariant);
        setSelectedVariant(invoicedVariant);
        hasInitializedRef.current = true;
      } else if (lowestPremiumVariant) {
        // console.log(
        //   "Initial selection - lowest variant:",
        //   lowestPremiumVariant
        // );
        setSelectedVariant(lowestPremiumVariant);
        hasInitializedRef.current = true;
      }
    }
  }, [lowestPremiumVariant, invoicedVariant, invoiceData]);

  const getStatusIcon = (quote: CascoQuoteData | null) => {
    if (!quote) {
      // No data received for this insurer
      return <InfoRegular style={{ fontSize: 16, color: "#8c8c8c" }} />;
    }
    if (quote.HasErrors) {
      return <ErrorCircleRegular style={{ fontSize: 16, color: "#ff4d4f" }} />;
    }
    if (quote.HasInsurerMessages || quote.HasInsurerWarnings) {
      return <WarningRegular style={{ fontSize: 16, color: "#faad14" }} />;
    }
    if (quote.HasPrices && quote.Variants.length > 0) {
      return (
        <CheckmarkCircleRegular style={{ fontSize: 16, color: "#52c41a" }} />
      );
    }
    // Return placeholder div to maintain alignment
    return <div style={{ width: 16, height: 16 }} />;
  };

  // Using centralized formatDuration from numberFormatting utils

  // Get all unique payment options across all quotes, variants, AND requested payments
  const getAllPaymentOptions = (): number[] => {
    const paymentSet = new Set<number>();
    // Include originally requested payments first (ensure they're all displayed)
    if (requestedPayments && requestedPayments.length > 0) {
      requestedPayments.forEach((p) => paymentSet.add(p));
    }
    // Include payments from quotes
    mergedQuotes.forEach((quote) => {
      quote.Variants.forEach((variant) => {
        if (variant?.Premiums) {
          variant.Premiums.forEach((p) => paymentSet.add(p.Payments));
        }
      });
    });
    return Array.from(paymentSet).sort((a, b) => a - b);
  };

  // Build complete list of insurers to display (requested + returned quotes)
  const allInsurersToDisplay = useMemo(() => {
    const insurerMap: {
      [key: string]: { name: string; quote: CascoQuoteData | null };
    } = {};

    // First, add all requested insurers (these should always be displayed)
    if (requestedInsurers && requestedInsurers.length > 0) {
      requestedInsurers.forEach((insurer) => {
        const key = insurer.toLowerCase();
        insurerMap[key] = { name: insurer, quote: null };
      });
    }

    // Then, add/update with actual quotes data
    mergedQuotes.forEach((quote) => {
      const key = quote.Insurer.toLowerCase();
      // Use the quote's insurer name (might have different casing)
      // Always show CASCO_VARIANTS_COUNT variants - missing ones get placeholders so their fields stay editable
      const variants = withPlaceholderVariants(quote.Variants ?? []);
      insurerMap[key] = {
        name: quote.Insurer,
        quote: variants === quote.Variants ? quote : { ...quote, Variants: variants },
      };
    });

    // Convert to array sorted by insurer name
    return Object.values(insurerMap).sort((a, b) =>
      a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
    );
  }, [mergedQuotes, requestedInsurers]);

  const getPremiumForPayments = (
    variant: CascoVariant,
    payments: number,
  ): number | null => {
    if (!variant?.Premiums) return null;
    const premium = variant.Premiums.find((p) => p.Payments === payments);
    return premium ? premium.ClientPremium : null;
  };

  // Helper: Find updated quote value for a specific insurer/variant/payments combination
  const getUpdatedQuote = useCallback(
    (
      insurer: string,
      variantID: number,
      payments: number,
    ): QuoteUpdateDto | undefined => {
      return updatedQuotes.find(
        (uq) =>
          uq.Insurer.toLowerCase() === insurer.toLowerCase() &&
          uq.VariantID === variantID &&
          uq.Payments === payments,
      );
    },
    [updatedQuotes],
  );

  const getPremiumDataForPayments = (
    quote: CascoQuoteData,
    variant: CascoVariant,
    payments: number,
  ): {
    premium: number | null;
    originalPremium: number | null;
    isUpdated: boolean;
  } => {
    const updatedQuote = getUpdatedQuote(quote.Insurer, variant.ID, payments);

    // First try to get original premium from stored originalPremiums map
    const storedOriginalKey = `${quote.Insurer}-${variant.ID}-${payments}`;
    const storedOriginal = originalPremiums?.get(storedOriginalKey);

    console.log(`🔍 Looking up original for key: "${storedOriginalKey}"`);
    console.log(
      `   Available keys in map:`,
      originalPremiums
        ? Array.from(originalPremiums.keys())
        : "map is undefined",
    );
    console.log(`   Found stored original:`, storedOriginal);

    // Determine the original premium value:
    // 1. Use stored original if available (this persists across backend updates)
    // 2. Otherwise fall back to variant data
    let originalPremium: number | null;
    if (storedOriginal !== undefined) {
      originalPremium = storedOriginal;
    } else {
      originalPremium = getPremiumForPayments(variant, payments);
    }

    // Check if premium was actually overridden (including null for "not offered")
    // Use !== undefined to allow null as a valid updated value
    if (
      updatedQuote &&
      updatedQuote.Premium !== undefined &&
      updatedQuote.Premium !== originalPremium
    ) {
      return {
        premium: updatedQuote.Premium,
        originalPremium,
        isUpdated: true,
      };
    }

    return {
      premium: originalPremium,
      originalPremium,
      isUpdated: false,
    };
  };

  // Get deductible field data with update check
  const getDeductibleFieldData = (
    insurer: string,
    variantID: number,
    fieldName: keyof Pick<
      QuoteUpdateDto,
      | "Deductible"
      | "DeductibleSecondAccident"
      | "DeductibleGlass"
      | "DeductibleTheft"
      | "DeductibleTotalLoss"
    >,
    originalValue: string | number | null | undefined,
    payments: number,
  ): {
    value: string | number | null | undefined;
    originalValue: string | number | null | undefined;
    isUpdated: boolean;
  } => {
    const updatedQuote = getUpdatedQuote(insurer, variantID, payments);

    // Check if the field has been updated (including null as a valid value)
    if (updatedQuote && updatedQuote[fieldName] !== undefined) {
      const updatedValue = updatedQuote[fieldName];

      // Normalize values for comparison (trim strings, treat empty/null/undefined as equivalent)
      const normalizeValue = (
        v: string | number | null | undefined,
      ): string | null => {
        if (v === undefined || v === null || v === "") return null;
        return typeof v === "string" ? v.trim() : String(v);
      };

      const normalizedUpdated = normalizeValue(updatedValue);
      const normalizedOriginal = normalizeValue(originalValue);

      // Only mark as updated if the value actually changed from original
      if (normalizedUpdated !== normalizedOriginal) {
        return {
          value: updatedValue,
          originalValue,
          isUpdated: true,
        };
      }
    }

    return {
      value: originalValue,
      originalValue,
      isUpdated: false,
    };
  };

  // Get transporting expenses data with update check
  const getTransportingExpensesData = (
    insurer: string,
    originalValue: string | number | null | undefined,
  ): {
    value: string | null | undefined;
    originalValue: string | number | null | undefined;
    isUpdated: boolean;
  } => {
    // Find any update for this insurer (transporting expenses applies to all variants/payments)
    const insurerUpdate = updatedQuotes.find(
      (u) => u.Insurer.toLowerCase() === insurer.toLowerCase(),
    );

    // Only process if TransportingExpenses is explicitly provided (not undefined)
    if (insurerUpdate && insurerUpdate.TransportingExpenses !== undefined) {
      const updatedValue = insurerUpdate.TransportingExpenses;

      // Normalize both values to strings for comparison
      // Handle null/empty as equivalent, convert numbers to strings
      const normalizeValue = (
        val: string | number | null | undefined,
      ): string | null => {
        if (val === undefined || val === null || val === "") return null;
        return String(val).trim();
      };

      const normalizedUpdated = normalizeValue(updatedValue);
      const normalizedOriginal = normalizeValue(originalValue);

      // Only mark as updated if the string values actually changed
      const valuesAreDifferent = normalizedUpdated !== normalizedOriginal;

      if (valuesAreDifferent) {
        return {
          value: normalizedUpdated,
          originalValue,
          isUpdated: true,
        };
      }
    }

    return {
      value: originalValue != null ? String(originalValue) : null,
      originalValue,
      isUpdated: false,
    };
  };

  // Get variant field data with update check (deductibles)
  // This returns the updated value if available, otherwise the original
  const getUpdatedVariantField = (
    insurer: string,
    variantID: number,
    payments: number,
    fieldName: keyof Pick<
      QuoteUpdateDto,
      | "Deductible"
      | "DeductibleSecondAccident"
      | "DeductibleGlass"
      | "DeductibleTheft"
      | "DeductibleTotalLoss"
    >,
    originalValue: string | undefined,
  ): string | undefined => {
    const updatedQuote = getUpdatedQuote(insurer, variantID, payments);
    if (updatedQuote && updatedQuote[fieldName] !== null) {
      return updatedQuote[fieldName] ?? undefined;
    }
    return originalValue;
  };

  // Get updated transporting expenses value
  const getUpdatedTransportingExpenses = (
    insurer: string,
    originalValue: string | number | null | undefined,
  ): string | undefined => {
    const teData = getTransportingExpensesData(insurer, originalValue);
    return teData.value ?? undefined;
  };

  // Handle edit button click
  const handleEditClick = (
    insurer: string,
    variantID: number,
    variant: CascoVariant,
    payments: number,
    premiumData: { premium: number | null; originalPremium: number | null },
  ) => {
    if (!canEdit || !onQuoteUpdate) return;

    const quote = mergedQuotes.find(
      (q) => q.Insurer.toLowerCase() === insurer.toLowerCase(),
    );
    const period =
      variant.Premiums?.find((p) => p.Payments === payments)?.Period || 12;

    setEditingQuote({
      insurer,
      period,
      payments,
      variantID,
      originalPremium: premiumData.originalPremium,
      currentPremium: premiumData.premium,
      deductible: variant.Deductible,
      deductibleSecondAccident: variant.DeductibleSecondAccident,
      deductibleTheft: variant.DeductibleTheft,
      deductibleTotalLoss: variant.DeductibleTotalLoss,
      deductibleGlass: variant.DeductibleGlass,
      transportingExpenses:
        quote?.TransportingExpenses != null
          ? String(quote.TransportingExpenses)
          : null,
    });
    setIsEditDialogOpen(true);
  };

  // Handle save from edit dialog
  const handleSaveEdit = (update: QuoteUpdateDto) => {
    if (onQuoteUpdate && editingQuote) {
      onQuoteUpdate(update, editingQuote.originalPremium);
    }
    setIsEditDialogOpen(false);
    setEditingQuote(null);
  };

  // Handle deductibles edit button click
  const handleDeductiblesEditClick = (
    insurer: string,
    variantID: number,
    variantIndex: number,
    variant: CascoVariant,
    payments: number,
  ) => {
    if (!canEdit || !onQuoteUpdate) return;

    // Get current values (potentially updated)
    const deductibleData = getDeductibleFieldData(
      insurer,
      variantID,
      "Deductible",
      variant.Deductible,
      payments,
    );
    const deductibleSecondAccidentData = getDeductibleFieldData(
      insurer,
      variantID,
      "DeductibleSecondAccident",
      variant.DeductibleSecondAccident,
      payments,
    );
    const deductibleGlassData = getDeductibleFieldData(
      insurer,
      variantID,
      "DeductibleGlass",
      variant.DeductibleGlass,
      payments,
    );
    const deductibleTheftData = getDeductibleFieldData(
      insurer,
      variantID,
      "DeductibleTheft",
      variant.DeductibleTheft,
      payments,
    );
    const deductibleTotalLossData = getDeductibleFieldData(
      insurer,
      variantID,
      "DeductibleTotalLoss",
      variant.DeductibleTotalLoss,
      payments,
    );

    setEditingDeductibles({
      insurer,
      variantID,
      variantIndex,
      deductible:
        typeof deductibleData.value === "string" ? deductibleData.value : null,
      deductibleSecondAccident:
        typeof deductibleSecondAccidentData.value === "string"
          ? deductibleSecondAccidentData.value
          : null,
      deductibleGlass:
        typeof deductibleGlassData.value === "string"
          ? deductibleGlassData.value
          : null,
      deductibleTheft:
        typeof deductibleTheftData.value === "string"
          ? deductibleTheftData.value
          : null,
      deductibleTotalLoss:
        typeof deductibleTotalLossData.value === "string"
          ? deductibleTotalLossData.value
          : null,
    });
    setIsDeductiblesDialogOpen(true);
  };

  // Handle save from deductibles dialog
  const handleSaveDeductibles = (updates: Partial<QuoteUpdateDto>) => {
    // This callback will be handled by the parent (CascoQuotesTab) which will apply
    // the updates to all payment options for this variant
    if (onQuoteUpdate && editingDeductibles) {
      // Create update object with deductible fields - parent will fill in Premium and TransportingExpenses
      // We only include the fields we're actually editing (deductibles)
      const fullUpdate: Partial<QuoteUpdateDto> & {
        Insurer: string;
        VariantID: number;
      } = {
        Insurer: editingDeductibles.insurer,
        VariantID: editingDeductibles.variantID,
        // Include deductible fields - use new value if changed, otherwise current value
        Deductible:
          "Deductible" in updates
            ? (updates.Deductible ?? null)
            : (editingDeductibles.deductible ?? null),
        DeductibleSecondAccident:
          "DeductibleSecondAccident" in updates
            ? (updates.DeductibleSecondAccident ?? null)
            : (editingDeductibles.deductibleSecondAccident ?? null),
        DeductibleGlass:
          "DeductibleGlass" in updates
            ? (updates.DeductibleGlass ?? null)
            : (editingDeductibles.deductibleGlass ?? null),
        DeductibleTheft:
          "DeductibleTheft" in updates
            ? (updates.DeductibleTheft ?? null)
            : (editingDeductibles.deductibleTheft ?? null),
        DeductibleTotalLoss:
          "DeductibleTotalLoss" in updates
            ? (updates.DeductibleTotalLoss ?? null)
            : (editingDeductibles.deductibleTotalLoss ?? null),
      };

      // Cast to QuoteUpdateDto - parent will handle filling in missing fields (Premium, TransportingExpenses, etc.)
      onQuoteUpdate(fullUpdate as QuoteUpdateDto, null);
    }
    setIsDeductiblesDialogOpen(false);
    setEditingDeductibles(null);
  };

  // Handle reset from deductibles dialog - removes all variant field updates
  const handleResetDeductibles = () => {
    if (onResetDeductibles && editingDeductibles) {
      onResetDeductibles(
        editingDeductibles.insurer,
        editingDeductibles.variantID,
      );
    }
    setIsDeductiblesDialogOpen(false);
    setEditingDeductibles(null);
  };

  // Handle transporting expenses edit button click
  const handleTransportingExpensesEditClick = (
    insurer: string,
    transportingExpenses: string | null | undefined,
  ) => {
    if (!canEdit || !onQuoteUpdate) return;

    const transportingExpensesData = getTransportingExpensesData(
      insurer,
      transportingExpenses,
    );

    setEditingTransportingExpenses({
      insurer,
      transportingExpenses: transportingExpensesData.value ?? null,
    });
    setIsTransportingExpensesDialogOpen(true);
  };

  // Handle save from transporting expenses dialog
  const handleSaveTransportingExpenses = (
    transportingExpenses: string | null,
  ) => {
    // This callback will be handled by the parent (CascoQuotesTab) which will apply
    // the update to all variants and payment options for this insurer
    if (onQuoteUpdate && editingTransportingExpenses) {
      // Create update object with TransportingExpenses - parent will fill in other fields
      // for all variants/payments. We pass undefined (not null) for fields we're not editing
      // so parent knows to preserve existing values
      const fullUpdate: Partial<QuoteUpdateDto> & {
        Insurer: string;
        TransportingExpenses: string | null;
      } = {
        Insurer: editingTransportingExpenses.insurer,
        TransportingExpenses: transportingExpenses,
      };

      // Cast to QuoteUpdateDto - parent will handle filling in missing fields
      onQuoteUpdate(fullUpdate as QuoteUpdateDto, null);
    }
    setIsTransportingExpensesDialogOpen(false);
    setEditingTransportingExpenses(null);
  };

  // Get unique error messages (deduplication)
  const getUniqueErrors = (errors: CascoError[]): CascoError[] => {
    const seen = new Set<string>();
    return errors.filter((error) => {
      if (seen.has(error.Message)) return false;
      seen.add(error.Message);
      return true;
    });
  };

  // Separate messages from errors, merge from both quote and saved
  const getMessages = (
    quote: CascoQuoteData,
  ): { message: string; source: string; code: number }[] => {
    const messages: { message: string; source: string; code: number }[] = [];

    // Add regular quote messages
    if (quote.HasInsurerMessages && quote.ErrorList) {
      quote.ErrorList.filter((err) => err.Code === 124).forEach((err) => {
        messages.push({
          message: err.Message,
          source: "Quote",
          code: err.Code,
        });
      });
    }

    // Add saved quote messages for this insurer
    const insurerKey = quote.Insurer.toLowerCase();
    cascoSaved.forEach((saved) => {
      if (
        saved.Insurer.toLowerCase() === insurerKey &&
        saved.HasInsurerMessages &&
        saved.ErrorList
      ) {
        saved.ErrorList.filter((err) => err.Code === 124).forEach((err) => {
          // Only add if not already present from regular quote
          if (!messages.some((m) => m.message === err.Message)) {
            messages.push({
              message: err.Message,
              source: "Saved",
              code: err.Code,
            });
          }
        });
      }
    });

    return messages;
  };

  // Get all errors and exceptions combined (Casco has no saving phase, so simpler display)
  const getErrors = (
    quote: CascoQuoteData,
  ): Array<{
    message: string;
    isException: boolean;
    stackTrace?: string;
  }> => {
    const result: Array<{
      message: string;
      isException: boolean;
      stackTrace?: string;
    }> = [];

    // Only get errors if the quote has error flags set
    if (
      quote.HasErrors ||
      quote.HasInsurerErrors ||
      quote.HasInsurerWarnings ||
      quote.HasValidationErrors
    ) {
      // Add all errors from ErrorList (including warnings, but filter out Code 124 which are informational messages)
      const errors = getUniqueErrors(
        quote.ErrorList.filter((err) => err.Code !== 124),
      );
      errors.forEach((err) => {
        result.push({
          message: err.Message,
          isException: false,
        });
      });
    }

    // Add exceptions (these indicate technical errors)
    const exceptions = quote.ExceptionList || [];
    exceptions.forEach((exc) => {
      // Only add if not already present
      if (!result.some((r) => r.message === exc.GenericMessage)) {
        result.push({
          message: exc.GenericMessage,
          isException: true,
          stackTrace: exc.GenericStackTrace,
        });
      }
    });

    return result;
  };

  const isVariantSelected = (
    insurer: string,
    variantIndex: number,
  ): boolean => {
    return selectedVariant === `${insurer}-${variantIndex}`;
  };

  // Build the selection passed to the parent (updated values if available, otherwise original)
  const buildSelection = (
    insurer: string,
    variantIndex: number,
    payments: number,
  ): Parameters<
    NonNullable<CascoQuotesDisplayProps["onSelectionChange"]>
  >[0] => {
    const quote = quotes.find((q) => q.Insurer === insurer);
    if (!quote) return null;

    // Same variants as displayed, including placeholders (see allInsurersToDisplay)
    const variant = withPlaceholderVariants(quote.Variants)[variantIndex];
    if (!variant) return null;

    const originalPremiumData = variant.Premiums.find(
      (p) => p.Payments === payments,
    );
    // Manually entered values (placeholder or added payment option) only exist as updates
    const updatedQuote = getUpdatedQuote(insurer, variant.ID, payments);
    if (!originalPremiumData && !updatedQuote) return null;

    const premiumData = getPremiumDataForPayments(quote, variant, payments);

    // Pass selection even if premium is null (for "not offered" quotes)
    return {
      insurer,
      variantIndex,
      variantID: variant.ID, // Include the actual variant ID
      premium: premiumData.premium, // Use updated premium if available (can be null)
      payments,
      period: originalPremiumData?.Period ?? updatedQuote?.Period ?? 12,
      deductible: getUpdatedVariantField(
        insurer,
        variant.ID,
        payments,
        "Deductible",
        variant.Deductible,
      ),
      deductibleSecondAccident: getUpdatedVariantField(
        insurer,
        variant.ID,
        payments,
        "DeductibleSecondAccident",
        variant.DeductibleSecondAccident,
      ),
      deductibleTheft: getUpdatedVariantField(
        insurer,
        variant.ID,
        payments,
        "DeductibleTheft",
        variant.DeductibleTheft,
      ),
      deductibleTotalLoss: getUpdatedVariantField(
        insurer,
        variant.ID,
        payments,
        "DeductibleTotalLoss",
        variant.DeductibleTotalLoss,
      ),
      deductibleGlass: getUpdatedVariantField(
        insurer,
        variant.ID,
        payments,
        "DeductibleGlass",
        variant.DeductibleGlass,
      ),
      securitySystem: variant.SecuritySystem ?? undefined,
      territory: quote.Territory ?? undefined,
      transportingExpenses: getUpdatedTransportingExpenses(
        insurer,
        quote.TransportingExpenses,
      ),
      insurerDiscount: originalPremiumData?.InsurerDiscount ?? 0,
      commissionDiscount: originalPremiumData?.CommissionDiscount ?? 0,
    };
  };

  const handleVariantSelection = (
    insurer: string,
    variantIndex: number,
    payments: number,
  ) => {
    const variantKey = `${insurer}-${variantIndex}-${payments}`;
    setSelectedVariant(variantKey);

    if (onSelectionChange) {
      onSelectionChange(buildSelection(insurer, variantIndex, payments));
    }
  };

  // Notify parent of initial selection
  useEffect(() => {
    if (selectedVariant && onSelectionChange) {
      const parts = selectedVariant.split("-");
      if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
        const selection = buildSelection(
          parts[0],
          parseInt(parts[1], 10),
          parseInt(parts[2], 10),
        );
        if (selection) {
          onSelectionChange(selection);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant, quotes, updatedQuotes]);

  // Only show "No Quotes Available" if we have neither quotes nor requested insurers
  if (
    (!quotes || quotes.length === 0) &&
    (!requestedInsurers || requestedInsurers.length === 0)
  ) {
    return (
      <NoQuotesMessage>
        <InfoRegular />
        <h3>{t("qtg.casco.noQuotes.title", "No Quotes Available")}</h3>
        <p>
          {t(
            "qtg.casco.noQuotes.description",
            "Select insurers and click 'Get Quotes' to see results",
          )}
        </p>
      </NoQuotesMessage>
    );
  }

  const paymentOptions = getAllPaymentOptions();

  return (
    <Container>
      <TableContainer>
        <Table>
          <TableHeader>
            <tr>
              <th></th>
              {allInsurersToDisplay.map(({ name, quote }) => {
                const insurerExcluded = isInsurerExcluded(name);
                // Variants actually priced by the insurer (empty/placeholder ones not counted)
                const offeredVariantsCount =
                  quote?.Variants?.filter((v) => v.Premiums?.length > 0)
                    .length ?? 0;
                return (
                  <ExcludedHeader
                    key={name}
                    colSpan={quote?.Variants?.length || 1}
                    style={{ borderRight: "2px solid #ccc" }}
                    excluded={insurerExcluded}
                  >
                    <InsurerHeader>
                      {canEdit && onExclusionChange && (
                        <InsurerCheckboxWrapper>
                          <Tooltip
                            content={
                              insurerExcluded
                                ? t(
                                    "qtg.casco.table.includeInsurer",
                                    "Include insurer in offer",
                                  )
                                : t(
                                    "qtg.casco.table.excludeInsurer",
                                    "Exclude insurer from offer",
                                  )
                            }
                            relationship="label"
                          >
                            <Checkbox
                              checked={!insurerExcluded}
                              onChange={() =>
                                handleInsurerExclusionToggle(name)
                              }
                              size="medium"
                            />
                          </Tooltip>
                        </InsurerCheckboxWrapper>
                      )}
                      {getStatusIcon(quote)}
                      <InsurerName>{name}</InsurerName>
                      {quote && offeredVariantsCount > 0 && (
                        <Badge
                          appearance="outline"
                          color={quote.HasErrors ? "danger" : "success"}
                          size="small"
                          style={{ fontSize: "9px", padding: "0 4px" }}
                        >
                          {offeredVariantsCount} variant
                          {offeredVariantsCount > 1 ? "s" : ""}
                        </Badge>
                      )}
                      {!quote && (
                        <Badge
                          appearance="outline"
                          color="informative"
                          size="small"
                          style={{ fontSize: "9px", padding: "0 4px" }}
                        >
                          {t("qtg.casco.table.noData", "No data")}
                        </Badge>
                      )}
                    </InsurerHeader>
                  </ExcludedHeader>
                );
              })}
            </tr>
            {/* Variant numbers sub-header */}
            <tr>
              <th></th>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const variantExcluded = isVariantExcluded(name, variant.ID);
                    const insurerExcluded = isInsurerExcluded(name);
                    return (
                      <ExcludedHeader
                        key={`${name}-${variantIndex}`}
                        style={{
                          fontSize: "10px",
                          fontWeight: "normal",
                          padding: "4px",
                          background:
                            !variantExcluded &&
                            isVariantSelected(name, variantIndex)
                              ? "#e6f7ff"
                              : "inherit",
                        }}
                        excluded={variantExcluded || insurerExcluded}
                      >
                        {canEdit && onExclusionChange && (
                          <VariantCheckboxWrapper>
                            <Tooltip
                              content={
                                variantExcluded
                                  ? t(
                                      "qtg.casco.table.includeVariant",
                                      "Include variant in offer",
                                    )
                                  : t(
                                      "qtg.casco.table.excludeVariant",
                                      "Exclude variant from offer",
                                    )
                              }
                              relationship="label"
                            >
                              <Checkbox
                                checked={!variantExcluded && !insurerExcluded}
                                disabled={insurerExcluded}
                                onChange={() =>
                                  handleVariantExclusionToggle(name, variant.ID)
                                }
                              />
                            </Tooltip>
                          </VariantCheckboxWrapper>
                        )}
                        V{variantIndex + 1}
                      </ExcludedHeader>
                    );
                  })
                ) : (
                  <th key={`${name}-empty`}></th>
                ),
              )}
            </tr>
          </TableHeader>

          <TableBody>
            {/* Deductibles Section */}
            <TableRow isSection>
              <td>{t("qtg.casco.table.deductibles", "Deductibles")}</td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((_, variantIndex) => (
                    <td key={`${name}-${variantIndex}-deductibles`}></td>
                  ))
                ) : (
                  <td key={`${name}-empty-deductibles`}></td>
                ),
              )}
            </TableRow>

            <TableRow>
              <td>
                <RowLabel>
                  {t("qtg.casco.table.deductible", "Standard Deductible")}
                </RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const deductibleData = getDeductibleFieldData(
                      name,
                      variant.ID,
                      "Deductible",
                      variant.Deductible,
                      1,
                    );
                    const insurerKey = name.toUpperCase();
                    const isExcluded =
                      isInsurerExcluded(insurerKey) ||
                      isVariantExcluded(insurerKey, variant.ID);

                    return (
                      <ExcludedCell
                        as="td"
                        key={`${name}-${variantIndex}`}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {deductibleData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${deductibleData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {deductibleData.value || "—"}
                                </UpdatedValue>
                              </Tooltip>
                            ) : (
                              variant.Deductible || <NoDataCell>—</NoDataCell>
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editDeductible",
                                "Edit deductible",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  deductibleData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleDeductiblesEditClick(
                                    name,
                                    variant.ID,
                                    variantIndex,
                                    variant,
                                    1,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : (
                          variant.Deductible || <NoDataCell>—</NoDataCell>
                        )}
                      </ExcludedCell>
                    );
                  })
                ) : (
                  <td key={`${name}-empty`}>
                    <NoDataCell>—</NoDataCell>
                  </td>
                ),
              )}
            </TableRow>

            <TableRow>
              <td>
                <RowLabel>
                  {t("qtg.casco.table.secondAccident", "Second Accident")}
                </RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const deductibleData = getDeductibleFieldData(
                      name,
                      variant.ID,
                      "DeductibleSecondAccident",
                      variant.DeductibleSecondAccident,
                      1,
                    );
                    const insurerKey = name.toUpperCase();
                    const isExcluded =
                      isInsurerExcluded(insurerKey) ||
                      isVariantExcluded(insurerKey, variant.ID);

                    return (
                      <ExcludedCell
                        as="td"
                        key={`${name}-${variantIndex}`}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {deductibleData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${deductibleData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {deductibleData.value || "—"}
                                </UpdatedValue>
                              </Tooltip>
                            ) : (
                              variant.DeductibleSecondAccident || (
                                <NoDataCell>—</NoDataCell>
                              )
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editDeductible",
                                "Edit deductible",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  deductibleData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleDeductiblesEditClick(
                                    name,
                                    variant.ID,
                                    variantIndex,
                                    variant,
                                    1,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : (
                          variant.DeductibleSecondAccident || (
                            <NoDataCell>—</NoDataCell>
                          )
                        )}
                      </ExcludedCell>
                    );
                  })
                ) : (
                  <td key={`${name}-empty`}>
                    <NoDataCell>—</NoDataCell>
                  </td>
                ),
              )}
            </TableRow>

            <TableRow>
              <td>
                <RowLabel>{t("qtg.casco.table.glass", "Glass")}</RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const deductibleData = getDeductibleFieldData(
                      name,
                      variant.ID,
                      "DeductibleGlass",
                      variant.DeductibleGlass,
                      1,
                    );
                    const insurerKey = name.toUpperCase();
                    const isExcluded =
                      isInsurerExcluded(insurerKey) ||
                      isVariantExcluded(insurerKey, variant.ID);

                    return (
                      <ExcludedCell
                        as="td"
                        key={`${name}-${variantIndex}`}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {deductibleData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${deductibleData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {deductibleData.value || "—"}
                                </UpdatedValue>
                              </Tooltip>
                            ) : (
                              variant.DeductibleGlass || (
                                <NoDataCell>—</NoDataCell>
                              )
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editDeductible",
                                "Edit deductible",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  deductibleData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleDeductiblesEditClick(
                                    name,
                                    variant.ID,
                                    variantIndex,
                                    variant,
                                    1,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : (
                          variant.DeductibleGlass || <NoDataCell>—</NoDataCell>
                        )}
                      </ExcludedCell>
                    );
                  })
                ) : (
                  <td key={`${name}-empty`}>
                    <NoDataCell>—</NoDataCell>
                  </td>
                ),
              )}
            </TableRow>

            <TableRow>
              <td>
                <RowLabel>{t("qtg.casco.table.theft", "Theft")}</RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const deductibleData = getDeductibleFieldData(
                      name,
                      variant.ID,
                      "DeductibleTheft",
                      variant.DeductibleTheft,
                      1,
                    );
                    const insurerKey = name.toUpperCase();
                    const isExcluded =
                      isInsurerExcluded(insurerKey) ||
                      isVariantExcluded(insurerKey, variant.ID);

                    return (
                      <ExcludedCell
                        as="td"
                        key={`${name}-${variantIndex}`}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {deductibleData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${deductibleData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {deductibleData.value || "—"}
                                </UpdatedValue>
                              </Tooltip>
                            ) : (
                              variant.DeductibleTheft || (
                                <NoDataCell>—</NoDataCell>
                              )
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editDeductible",
                                "Edit deductible",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  deductibleData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleDeductiblesEditClick(
                                    name,
                                    variant.ID,
                                    variantIndex,
                                    variant,
                                    1,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : (
                          variant.DeductibleTheft || <NoDataCell>—</NoDataCell>
                        )}
                      </ExcludedCell>
                    );
                  })
                ) : (
                  <td key={`${name}-empty`}>
                    <NoDataCell>—</NoDataCell>
                  </td>
                ),
              )}
            </TableRow>

            <TableRow>
              <td>
                <RowLabel>
                  {t("qtg.casco.table.totalLoss", "Total Loss")}
                </RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) =>
                quote?.Variants && quote.Variants.length > 0 ? (
                  quote.Variants.map((variant, variantIndex) => {
                    const deductibleData = getDeductibleFieldData(
                      name,
                      variant.ID,
                      "DeductibleTotalLoss",
                      variant.DeductibleTotalLoss,
                      1,
                    );
                    const insurerKey = name.toUpperCase();
                    const isExcluded =
                      isInsurerExcluded(insurerKey) ||
                      isVariantExcluded(insurerKey, variant.ID);

                    return (
                      <ExcludedCell
                        as="td"
                        key={`${name}-${variantIndex}`}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {deductibleData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${deductibleData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {deductibleData.value || "—"}
                                </UpdatedValue>
                              </Tooltip>
                            ) : (
                              variant.DeductibleTotalLoss || (
                                <NoDataCell>—</NoDataCell>
                              )
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editDeductible",
                                "Edit deductible",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  deductibleData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleDeductiblesEditClick(
                                    name,
                                    variant.ID,
                                    variantIndex,
                                    variant,
                                    1,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : (
                          variant.DeductibleTotalLoss || (
                            <NoDataCell>—</NoDataCell>
                          )
                        )}
                      </ExcludedCell>
                    );
                  })
                ) : (
                  <td key={`${name}-empty`}>
                    <NoDataCell>—</NoDataCell>
                  </td>
                ),
              )}
            </TableRow>

            {/* Premiums Section */}
            {paymentOptions.length > 0 && (
              <>
                <TableRow isSection>
                  <td>{t("qtg.casco.table.premiums", "Premiums")}</td>
                  {allInsurersToDisplay.map(({ name, quote }) =>
                    quote?.Variants && quote.Variants.length > 0 ? (
                      quote.Variants.map((_, variantIndex) => (
                        <td key={`${name}-${variantIndex}-premiums`}></td>
                      ))
                    ) : (
                      <td key={`${name}-empty-premiums`}></td>
                    ),
                  )}
                </TableRow>

                {paymentOptions.map((payments) => (
                  <TableRow key={payments}>
                    <td>
                      <RowLabel>
                        {payments === 1
                          ? t("qtg.casco.table.annual", "Annual (1 payment)")
                          : `${payments} ${t(
                              "qtg.casco.table.payments",
                              "payments",
                            )}`}
                      </RowLabel>
                    </td>
                    {allInsurersToDisplay.map(({ name, quote }) =>
                      quote?.Variants && quote.Variants.length > 0 ? (
                        quote.Variants.map((variant, variantIndex) => {
                          const premiumData = getPremiumDataForPayments(
                            quote,
                            variant,
                            payments,
                          );
                          const variantKey = `${name}-${variantIndex}-${payments}`;
                          const isSelected = selectedVariant === variantKey;
                          const insurerKey = name.toUpperCase();
                          const isExcluded =
                            isInsurerExcluded(insurerKey) ||
                            isVariantExcluded(insurerKey, variant.ID);

                          // console.log("CASCO variant render:", {
                          //   insurer: name,
                          //   variantIndex,
                          //   payments,
                          //   variantKey,
                          //   selectedVariant,
                          //   isSelected,
                          //   premium: premiumData.premium,
                          //   isUpdated: premiumData.isUpdated,
                          // });

                          return (
                            <ExcludedCell
                              as="td"
                              key={variantKey}
                              excluded={isExcluded}
                              className={isSelected ? "selected-variant" : ""}
                            >
                              {premiumData.premium !== null ? (
                                <PremiumCell>
                                  {canEdit && onQuoteUpdate && !isExcluded && (
                                    <EditIconButton
                                      appearance="subtle"
                                      size="small"
                                      icon={<EditRegular />}
                                      onClick={() =>
                                        handleEditClick(
                                          name,
                                          variant.ID,
                                          variant,
                                          payments,
                                          premiumData,
                                        )
                                      }
                                      className={`edit-icon-button ${
                                        premiumData.isUpdated
                                          ? "always-visible"
                                          : ""
                                      }`}
                                      title={t(
                                        "qtg.casco.table.editPremium",
                                        "Edit premium value",
                                      )}
                                    />
                                  )}
                                  <RadioLabel>
                                    <input
                                      type="radio"
                                      name="variantSelection"
                                      value={variantKey}
                                      checked={selectedVariant === variantKey}
                                      disabled={
                                        isExcluded ||
                                        (invoiceData !== null &&
                                          !isInvoicedOption(
                                            name,
                                            variant.ID,
                                            payments,
                                          ))
                                      }
                                      onChange={() =>
                                        handleVariantSelection(
                                          name,
                                          variantIndex,
                                          payments,
                                        )
                                      }
                                    />
                                    {premiumData.isUpdated ? (
                                      <Tooltip
                                        content={`${t(
                                          "qtg.casco.table.originalValue",
                                          "Original",
                                        )}: €${formatCurrency(
                                          premiumData.originalPremium ?? 0,
                                        )}`}
                                        relationship="description"
                                      >
                                        <UpdatedValue>
                                          €{formatCurrency(premiumData.premium)}
                                        </UpdatedValue>
                                      </Tooltip>
                                    ) : (
                                      <PremiumValue>
                                        €{formatCurrency(premiumData.premium)}
                                      </PremiumValue>
                                    )}
                                  </RadioLabel>
                                </PremiumCell>
                              ) : (
                                <PremiumCell>
                                  {canEdit && onQuoteUpdate && !isExcluded && (
                                    <EditIconButton
                                      appearance="subtle"
                                      size="small"
                                      icon={<EditRegular />}
                                      onClick={() =>
                                        handleEditClick(
                                          name,
                                          variant.ID,
                                          variant,
                                          payments,
                                          premiumData,
                                        )
                                      }
                                      className={`edit-icon-button ${
                                        premiumData.isUpdated
                                          ? "always-visible"
                                          : ""
                                      }`}
                                      title={
                                        premiumData.isUpdated
                                          ? t(
                                              "qtg.casco.table.editPremium",
                                              "Edit premium value",
                                            )
                                          : t(
                                              "qtg.casco.table.addPremium",
                                              "Add premium value",
                                            )
                                      }
                                    />
                                  )}
                                  {premiumData.isUpdated ? (
                                    <Tooltip
                                      content={`${t(
                                        "qtg.casco.table.originalValue",
                                        "Original",
                                      )}: €${formatCurrency(
                                        premiumData.originalPremium ?? 0,
                                      )}`}
                                      relationship="description"
                                    >
                                      <UpdatedValue>—</UpdatedValue>
                                    </Tooltip>
                                  ) : (
                                    <NoDataCell>—</NoDataCell>
                                  )}
                                </PremiumCell>
                              )}
                            </ExcludedCell>
                          );
                        })
                      ) : (
                        <td key={`${name}-empty`}>
                          <NoDataCell>—</NoDataCell>
                        </td>
                      ),
                    )}
                  </TableRow>
                ))}
              </>
            )}

            {/* Additional Information */}
            {quotes.some((q) => q.TransportingExpenses) && (
              <TableRow>
                <td>
                  <RowLabel>
                    {t(
                      "qtg.casco.table.transportingExpenses",
                      "Transporting Expenses",
                    )}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote }) => {
                  if (quote?.Variants && quote.Variants.length > 0) {
                    const transportingExpensesData =
                      getTransportingExpensesData(
                        name,
                        quote.TransportingExpenses,
                      );
                    const insurerKey = name.toUpperCase();
                    const isExcluded = isInsurerExcluded(insurerKey);

                    return (
                      <ExcludedCell
                        as="td"
                        key={name}
                        colSpan={quote.Variants.length}
                        style={{ textAlign: "center" }}
                        excluded={isExcluded}
                      >
                        {canEdit && onQuoteUpdate && !isExcluded ? (
                          <PremiumCell>
                            {transportingExpensesData.isUpdated ? (
                              <Tooltip
                                content={`Original: ${transportingExpensesData.originalValue}`}
                                relationship="description"
                              >
                                <UpdatedValue>
                                  {transportingExpensesData.value}
                                </UpdatedValue>
                              </Tooltip>
                            ) : quote.TransportingExpenses ? (
                              quote.TransportingExpenses
                            ) : (
                              <NoDataCell>—</NoDataCell>
                            )}
                            <Tooltip
                              content={t(
                                "qtg.casco.table.editTransportingExpenses",
                                "Edit transporting expenses",
                              )}
                              relationship="label"
                            >
                              <EditIconButton
                                className={`edit-icon-button ${
                                  transportingExpensesData.isUpdated
                                    ? "always-visible"
                                    : ""
                                }`}
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                onClick={() =>
                                  handleTransportingExpensesEditClick(
                                    name,
                                    quote.TransportingExpenses != null
                                      ? String(quote.TransportingExpenses)
                                      : null,
                                  )
                                }
                              />
                            </Tooltip>
                          </PremiumCell>
                        ) : quote.TransportingExpenses ? (
                          `€${quote.TransportingExpenses}`
                        ) : (
                          <NoDataCell>—</NoDataCell>
                        )}
                      </ExcludedCell>
                    );
                  } else {
                    return (
                      <td key={`${name}-empty`}>
                        <NoDataCell>—</NoDataCell>
                      </td>
                    );
                  }
                })}
              </TableRow>
            )}

            {/* Security Requirements */}
            {quotes.some((q) => q.Variants.some((v) => v.SecuritySystem)) && (
              <TableRow>
                <td>
                  <RowLabel>
                    <InfoRegular style={{ fontSize: 12, color: "#1890ff" }} />
                    {t("qtg.casco.table.security", "Security Requirements")}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote }) =>
                  quote?.Variants && quote.Variants.length > 0 ? (
                    quote.Variants.map((variant, variantIndex) => {
                      const insurerKey = name.toUpperCase();
                      const isExcluded =
                        isInsurerExcluded(insurerKey) ||
                        isVariantExcluded(insurerKey, variant.ID);
                      return (
                        <ExcludedCell
                          as="td"
                          key={`${name}-${variantIndex}`}
                          excluded={isExcluded}
                        >
                          {variant?.SecuritySystem ? (
                            <Tooltip
                              content={variant.SecuritySystem}
                              relationship="description"
                            >
                              <SecurityNote>
                                {t(
                                  "qtg.casco.table.securityRequired",
                                  "Required",
                                )}
                              </SecurityNote>
                            </Tooltip>
                          ) : (
                            <NoDataCell>—</NoDataCell>
                          )}
                        </ExcludedCell>
                      );
                    })
                  ) : (
                    <td key={`${name}-empty`}>
                      <NoDataCell>—</NoDataCell>
                    </td>
                  ),
                )}
              </TableRow>
            )}

            {/* Warnings/Messages */}
            {allInsurersToDisplay.some(
              ({ quote }) => quote && getMessages(quote).length > 0,
            ) && (
              <TableRow>
                <td>
                  <RowLabel>
                    <WarningRegular
                      style={{ fontSize: 12, color: "#faad14" }}
                    />
                    {t("qtg.casco.table.messages", "Messages")}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote }) => {
                  const messages = quote ? getMessages(quote) : [];
                  const insurerKey = name.toUpperCase();
                  const isExcluded = isInsurerExcluded(insurerKey);
                  return (
                    <ExcludedCell
                      as="td"
                      key={name}
                      colSpan={quote?.Variants?.length || 1}
                      style={{ textAlign: "center" }}
                      excluded={isExcluded}
                    >
                      {messages.length > 0 ? (
                        <Tooltip
                          content={
                            <div
                              style={{
                                maxWidth: "400px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {messages.map((m, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom:
                                      idx < messages.length - 1 ? "8px" : "0",
                                  }}
                                >
                                  <strong>
                                    [{m.source}] Code {m.code}:
                                  </strong>{" "}
                                  {m.message}
                                </div>
                              ))}
                            </div>
                          }
                          relationship="description"
                        >
                          <WarningMessage>
                            {messages.length} message
                            {messages.length > 1 ? "s" : ""}
                          </WarningMessage>
                        </Tooltip>
                      ) : (
                        <NoDataCell>—</NoDataCell>
                      )}
                    </ExcludedCell>
                  );
                })}
              </TableRow>
            )}

            {/* Errors */}
            {allInsurersToDisplay.some(
              ({ quote }) => quote && getErrors(quote).length > 0,
            ) && (
              <TableRow hasError>
                <td>
                  <RowLabel>
                    <ErrorCircleRegular
                      style={{ fontSize: 12, color: "#ff4d4f" }}
                    />
                    {t("qtg.casco.table.errors", "Errors")}
                  </RowLabel>
                </td>
                {allInsurersToDisplay.map(({ name, quote }) => {
                  const errors = quote ? getErrors(quote) : [];
                  const insurerKey = name.toUpperCase();
                  const isExcluded = isInsurerExcluded(insurerKey);
                  return (
                    <ExcludedCell
                      as="td"
                      key={name}
                      colSpan={quote?.Variants?.length || 1}
                      style={{ textAlign: "center" }}
                      excluded={isExcluded}
                    >
                      {errors.length > 0 ? (
                        <Tooltip
                          content={
                            <div
                              style={{
                                maxWidth: "400px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {errors.map((e, idx) => (
                                <div
                                  key={idx}
                                  style={{
                                    marginBottom:
                                      idx < errors.length - 1 ? "8px" : "0",
                                  }}
                                >
                                  {e.isException && (
                                    <strong>(Exception) </strong>
                                  )}
                                  {e.message}
                                </div>
                              ))}
                            </div>
                          }
                          relationship="description"
                        >
                          <ErrorMessage>
                            {errors.length} error{errors.length > 1 ? "s" : ""}
                          </ErrorMessage>
                        </Tooltip>
                      ) : (
                        <NoDataCell>—</NoDataCell>
                      )}
                    </ExcludedCell>
                  );
                })}
              </TableRow>
            )}

            {/* Execution Time */}
            <TableRow>
              <td>
                <RowLabel>
                  {t("qtg.casco.table.executionTime", "Execution Time")}
                </RowLabel>
              </td>
              {allInsurersToDisplay.map(({ name, quote }) => {
                const insurerKey = name.toUpperCase();
                const isExcluded = isInsurerExcluded(insurerKey);
                return (
                  <ExcludedCell
                    as="td"
                    key={name}
                    colSpan={quote?.Variants?.length || 1}
                    style={{ textAlign: "center" }}
                    excluded={isExcluded}
                  >
                    {quote ? (
                      formatDuration(quote.Duration)
                    ) : (
                      <NoDataCell>—</NoDataCell>
                    )}
                  </ExcludedCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Premium Dialog */}
      {editingQuote && (
        <EditPremiumDialog
          open={isEditDialogOpen}
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingQuote(null);
          }}
          onSave={handleSaveEdit}
          quoteData={editingQuote}
          insuranceType="casco"
        />
      )}

      {/* Edit Deductibles Dialog */}
      {editingDeductibles && (
        <EditDeductiblesDialog
          open={isDeductiblesDialogOpen}
          onClose={() => {
            setIsDeductiblesDialogOpen(false);
            setEditingDeductibles(null);
          }}
          onSave={handleSaveDeductibles}
          onReset={handleResetDeductibles}
          insurer={editingDeductibles.insurer}
          variantIndex={editingDeductibles.variantIndex}
          deductible={editingDeductibles.deductible ?? undefined}
          deductibleSecondAccident={
            editingDeductibles.deductibleSecondAccident ?? undefined
          }
          deductibleGlass={editingDeductibles.deductibleGlass ?? undefined}
          deductibleTheft={editingDeductibles.deductibleTheft ?? undefined}
          deductibleTotalLoss={
            editingDeductibles.deductibleTotalLoss ?? undefined
          }
        />
      )}

      {/* Edit Transporting Expenses Dialog */}
      {editingTransportingExpenses && (
        <EditTransportingExpensesDialog
          open={isTransportingExpensesDialogOpen}
          onClose={() => {
            setIsTransportingExpensesDialogOpen(false);
            setEditingTransportingExpenses(null);
          }}
          onSave={handleSaveTransportingExpenses}
          insurer={editingTransportingExpenses.insurer}
          transportingExpenses={
            editingTransportingExpenses.transportingExpenses ?? undefined
          }
        />
      )}
    </Container>
  );
};

export default CascoQuotesDisplay;
